import { carouselHeroMessages } from './messages/carousel-hero.messages';

import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { PreloadedQuery, usePreloadedQuery } from 'react-relay';

import {
	AnchorPageRelayQuery,
	shapeAnchorContentfulResponse,
} from 'src/contentful/graphql/anchor-page';
import { anchorPageRelayQuery } from '@/__generated_contentful__/anchorPageRelayQuery.graphql';
import { CarouselProps } from '@/components/confluence/carousel/src/carousel';
import { useLogMeInD0to6 } from '@/components/confluence/hooks/use-log-me-in-d0to6.tsx';
import HeroSection from '@/templates/software/confluence/confluence-software-page/src/sections/log-me-in/hero';
import {
	AnchorContentsResponse,
	PaidLandingPersonaTypes,
} from '@/utils/anchor-queries/get-anchor-contents';
import { experiments } from '@/utils/experiment/manifest';
import { useExperimentExposure } from '@/utils/experiment/use-experiment-exposure';
import { FeatureExposed } from '@/utils/feature-exposed/use-feature-exposed';
import { ConfluenceSoftwarePageProps } from './confluence-software-page';
import { ConfluencePagePaidLpConflAnchor } from './paid-lp-confl-anchor';
import { isTargetedSubAnchor } from './subAnchors/subAnchor-util.tsx';

export interface ConfluenceSoftwarePageAnchorHrRelayProps extends ConfluenceSoftwarePageProps {
	anchorContents?: AnchorContentsResponse | null;
	persona?: string;
	queryRef?: PreloadedQuery<anchorPageRelayQuery>;
}
export const ConfluenceSoftwarePageAnchorHr = (
	pageProps: ConfluenceSoftwarePageAnchorHrRelayProps,
) => {
	const intl = useIntl();
	const [exposureSubAnchorFired, fireSubAnchorExposure] = useExperimentExposure(
		experiments.cfind4782LandingPageSubAnchorV3,
	);
	useEffect(() => {
		if (isTargetedSubAnchor(pageProps.subAnchor) && !exposureSubAnchorFired) {
			fireSubAnchorExposure();
		}
	}, [exposureSubAnchorFired, fireSubAnchorExposure]);

	const data = usePreloadedQuery(
		AnchorPageRelayQuery,
		pageProps.queryRef || (null as unknown as PreloadedQuery<anchorPageRelayQuery>),
	);
	pageProps = Object.assign({}, pageProps, {
		anchorContents: shapeAnchorContentfulResponse(data),
	});

	const { userProfileName, recentSites, shouldShowLogMeIn } = useLogMeInD0to6({
		availableProducts: pageProps.availableProducts,
		userProfile: pageProps.userProfile,
	});

	const carouselSlides = [
		{
			primaryText: intl.formatMessage({
				id: 'ConfluenceSoftwarePageTemplate.Hr.source-of-truth.title',
				defaultMessage: 'A single source of truth',
			}),
			description: intl.formatMessage({
				id: 'ConfluenceSoftwarePageTemplate.Hr.source-of-truth.body',
				defaultMessage:
					'Confluence brings together recruiting, onboarding, and employee development—all in one place.',
			}),
			imgSrc: 'https://dam-cdn.atl.orangelogic.com/AssetLink/6ow1y2k84sc71x364sfir34k2fu2k4o8.webp',
			imgAltText: 'Organized HR Hub in Confluence',
		},
		{
			primaryText: intl.formatMessage({
				id: 'ConfluenceSoftwarePageTemplate.Hr.scale-practices.title',
				defaultMessage: 'Scale best practices across your org',
			}),
			description: intl.formatMessage({
				id: 'ConfluenceSoftwarePageTemplate.Hr.scale-practices.body',
				defaultMessage:
					'Boost productivity by using Automation to coordinate workflows, prompting next steps when a dependency is completed.',
			}),
			imgSrc: 'https://dam-cdn.atl.orangelogic.com/AssetLink/1d4ro6h610bxw33m1v70c0ik55110tmg.webp',
			imgAltText: 'A Confluence automation rule',
		},
		{
			primaryText: intl.formatMessage({
				id: 'ConfluenceSoftwarePageTemplate.Hr.simplify-workflows.title',
				defaultMessage: 'Simplify HR workflows',
			}),
			description: intl.formatMessage({
				id: 'ConfluenceSoftwarePageTemplate.Hr.simplify-workflows.body',
				defaultMessage:
					'Brainstorm ideas for employee engagement, coordinate training, and share announcements with the entire org.',
			}),
			imgSrc: 'https://dam-cdn.atl.orangelogic.com/AssetLink/g46gcy7jab7kyu00487ys6bkiv0e7a1f.webp',
			imgAltText: 'Ideas on Confluence Sticky Notes',
		},
	];

	const carouselHeroProps: CarouselProps = {
		'aria-label': intl.formatMessage(carouselHeroMessages.ariaLabel),
		id: 'confluence-hr-carousel',
		slides: carouselSlides,
	};

	const pageId = 'anchor-lp-conf-team-hr';

	let customHeroSection = null;
	if (shouldShowLogMeIn) {
		customHeroSection = (
			<FeatureExposed experimentKey="cfind-2801-get-paid-users-back-to-product" group="Test">
				<HeroSection
					confluenceSites={recentSites}
					userProfileName={userProfileName}
					hideExploreMore
					loggedInPaid
				/>
			</FeatureExposed>
		);
	}

	return (
		<ConfluencePagePaidLpConflAnchor
			{...pageProps}
			pageId={pageId}
			persona={'unknown' as PaidLandingPersonaTypes}
			pageVariant="hr"
			dataProductKey="confluence-AnchorLpConfHr"
			carouselHeroProps={carouselHeroProps}
			keyword={pageProps.keyword}
			customHeroSection={customHeroSection}
		/>
	);
};
